import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cvAPI, interviewAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  MessageSquare, ChevronLeft, Loader2, FileText, ChevronDown, 
  ChevronUp, Lightbulb, AlertCircle, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

const InterviewPrepPage = () => {
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [cvs, setCvs] = useState([]);
  const [selectedCV, setSelectedCV] = useState('');
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [expandedQuestions, setExpandedQuestions] = useState({});

  useEffect(() => {
    loadCVs();
  }, []);

  const loadCVs = async () => {
    try {
      const response = await cvAPI.list();
      setCvs(response.data);
      if (response.data.length > 0) {
        setSelectedCV(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load CVs');
    }
  };

  const generateQuestions = async () => {
    if (!selectedCV) {
      toast.error('Please select a CV');
      return;
    }

    setLoading(true);
    setQuestions([]);

    try {
      const response = await interviewAPI.generateQuestions(selectedCV);
      setQuestions(response.data);
      toast.success('Questions generated!');
    } catch (error) {
      toast.error('Failed to generate questions');
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (id) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'General': return 'bg-blue-100 text-blue-700';
      case 'Experience': return 'bg-emerald-100 text-emerald-700';
      case 'Skills': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (!isSubscribed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Subscription Required</h1>
          <p className="text-slate-600 mb-6">Subscribe to access Interview Prep</p>
          <Button onClick={() => navigate('/subscribe')} className="bg-amber-500 hover:bg-amber-600">
            Subscribe Now
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 lg:px-8 h-16 flex items-center">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-slate-600"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Dashboard
          </Button>
          <div className="h-6 w-px bg-slate-200"></div>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-emerald-500" />
            <h1 className="font-semibold text-slate-900">Interview Prep</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Practice Interview Questions
            </h1>
            <p className="text-slate-600">
              AI-generated questions based on YOUR CV – no fake scenarios
            </p>
          </div>

          {cvs.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 mb-2">No CVs Found</h2>
              <p className="text-slate-600 mb-6">Create a CV first to generate interview questions</p>
              <Button onClick={() => navigate('/cv-builder')} className="bg-slate-900 hover:bg-slate-800">
                Create Your CV
              </Button>
            </div>
          ) : (
            <>
              {/* CV Selection */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
                <h2 className="font-semibold text-slate-900 mb-4">Select CV for Questions</h2>
                <div className="flex gap-4">
                  <Select value={selectedCV} onValueChange={setSelectedCV}>
                    <SelectTrigger data-testid="interview-cv-select" className="h-12 flex-1">
                      <SelectValue placeholder="Choose a CV" />
                    </SelectTrigger>
                    <SelectContent>
                      {cvs.map((cv) => (
                        <SelectItem key={cv.id} value={cv.id}>
                          {cv.title || cv.personal_details?.full_name || 'Untitled CV'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    data-testid="generate-questions-btn"
                    onClick={generateQuestions}
                    disabled={loading || !selectedCV}
                    className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <BookOpen className="w-5 h-5 mr-2" />
                        Generate Questions
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Questions List */}
              {questions.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="font-semibold text-slate-900">Interview Questions</h2>
                    <span className="text-sm text-slate-500">{questions.length} questions</span>
                  </div>

                  {questions.map((q, index) => (
                    <div 
                      key={q.id} 
                      data-testid={`question-${index}`}
                      className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                    >
                      <button
                        onClick={() => toggleQuestion(q.id)}
                        className="w-full p-5 flex items-start gap-4 text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-slate-600 font-semibold flex-shrink-0">
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(q.category)}`}>
                              {q.category}
                            </span>
                          </div>
                          <p className="font-medium text-slate-900">{q.question}</p>
                        </div>
                        {expandedQuestions[q.id] ? (
                          <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                      </button>
                      
                      {expandedQuestions[q.id] && q.suggested_answer && (
                        <div className="px-5 pb-5 pt-0">
                          <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Lightbulb className="w-4 h-4 text-emerald-600" />
                              <span className="text-sm font-medium text-emerald-700">Suggested Answer Points</span>
                            </div>
                            <p className="text-sm text-emerald-800">{q.suggested_answer}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {questions.length === 0 && !loading && (
                <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                  <p className="text-slate-500">
                    Select a CV and click "Generate Questions" to get started
                  </p>
                </div>
              )}
            </>
          )}

          {/* Info Box */}
          <div className="mt-8 bg-emerald-50 rounded-xl p-6 border border-emerald-200">
            <h3 className="font-semibold text-emerald-900 mb-2">About Interview Prep</h3>
            <ul className="text-emerald-800 text-sm space-y-2">
              <li>• Questions are generated based on your actual CV content</li>
              <li>• Suggested answers reference only what's in your CV</li>
              <li>• We never create fake scenarios or fictional experience</li>
              <li>• Practice answering honestly to build confidence</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
};

export default InterviewPrepPage;
